import React, { useEffect, useMemo, useRef } from "react";
import { Marker, Popup } from "react-leaflet";
import { useTheme } from "@material-ui/core/styles";

import { getMapStatus } from "../../domain/device";
import { getDeviceMarkerIcon } from "./mapIcons";
import MapMarkerPopup from "./MapMarkerPopup";

const POPUP_CLOSE_DELAY = 200;

const DeviceMarker = ({
  device,
  isHighlighted,
  sensorSettings,
  pinnedDeviceId,
  onPin,
  onUnpin
}) => {
  const theme = useTheme();
  const leafletRef = useRef();
  const closeTimeoutRef = useRef();
  const isPinned = pinnedDeviceId === device.id;

  useEffect(() => {
    if (!isHighlighted) {
      return;
    }

    leafletRef.current.openPopup();
  }, []);

  useEffect(() => () => clearTimeout(closeTimeoutRef.current), []);

  const status = getMapStatus(device);
  const color = theme.custom.colors.deviceStatus[status];
  const icon = useMemo(() => getDeviceMarkerIcon(status, color), [
    status,
    color
  ]);

  const handleMouseOver = () => {
    // Don't let a hover-preview elsewhere fight a popup the user explicitly pinned.
    if (pinnedDeviceId && pinnedDeviceId !== device.id) {
      return;
    }

    clearTimeout(closeTimeoutRef.current);
    leafletRef.current.openPopup();
  };

  const handleMouseOut = () => {
    if (isPinned) {
      return;
    }

    clearTimeout(closeTimeoutRef.current);
    closeTimeoutRef.current = setTimeout(() => {
      leafletRef.current.closePopup();
    }, POPUP_CLOSE_DELAY);
  };

  const handleClick = () => {
    clearTimeout(closeTimeoutRef.current);
    onPin(device.id);

    leafletRef.current.openPopup();
  };

  const handlePopupClose = () => {
    if (isPinned) {
      onUnpin(device.id);
    }
  };

  return (
    <Marker
      icon={icon}
      ref={leafletRef}
      position={[device.location.lat, device.location.lng]}
      eventHandlers={{
        click: handleClick,
        mouseover: handleMouseOver,
        mouseout: handleMouseOut,
        popupclose: handlePopupClose
      }}
    >
      <Popup>
        <MapMarkerPopup device={device} sensorSettings={sensorSettings} />
      </Popup>
    </Marker>
  );
};

export default React.memo(DeviceMarker, (prevProps, nextProps) => {
  return (
    prevProps.device.id === nextProps.device.id &&
    prevProps.device.status === nextProps.device.status &&
    prevProps.device.thresholdStatus === nextProps.device.thresholdStatus &&
    prevProps.device.location === nextProps.device.location &&
    prevProps.isHighlighted === nextProps.isHighlighted &&
    prevProps.sensorSettings === nextProps.sensorSettings &&
    prevProps.pinnedDeviceId === nextProps.pinnedDeviceId
  );
});
